import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import s from './style.module.scss';
const FluidOrb = ({ canvasId = 'orb-canvas', initialConfig = {}, text = '' }) => {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const animationRef = useRef(null);
    const pointsRef = useRef([]);
    const rotationRef = useRef({ y: 0, x: 0 });
    const timeRef = useRef(0);
    const sizeRef = useRef(300);

    const [config, setConfig] = useState({
        speed: 1.0,
        pointCount: 40,
        colorA: '#3b82f6',
        colorB: '#06b6d4',
        ...initialConfig
    });

    // 初始化canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return;

        ctxRef.current = ctx;
        sizeRef.current = 300;

        // 设置canvas尺寸
        const dpr = window.devicePixelRatio || 1;
        canvas.width = sizeRef.current * dpr;
        canvas.height = sizeRef.current * dpr;
        ctx.scale(dpr, dpr);
        canvas.style.width = `${sizeRef.current}px`;
        canvas.style.height = `${sizeRef.current}px`;

        // 生成初始点
        generatePoints(config.pointCount);

        // 开始动画
        startAnimation();

        // 清理函数
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    // 当配置更新时重新生成点或更新参数
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !ctxRef.current) return;

        // 检查是否需要重新生成点
        const regen = config.pointCount !== pointsRef.current.length;
        if (regen) {
            generatePoints(config.pointCount);
        }
    }, [config]);

    // 生成点
    const generatePoints = (count) => {
        const points = [];
        const radius = 100;
        const phi = Math.PI * (3 - Math.sqrt(5));

        for (let i = 0; i < count; i++) {
            const y = 1 - (i / (count - 1)) * 2;
            const rAtY = Math.sqrt(1 - y * y);
            const theta = phi * i;

            points.push({
                base: {
                    x: Math.cos(theta) * rAtY * radius,
                    y: y * radius,
                    z: Math.sin(theta) * rAtY * radius
                },
                offsets: [
                    { phase: Math.random() * Math.PI * 2, freq: 0.8 + Math.random() * 0.4, amp: 4 + Math.random() * 3 },
                    { phase: Math.random() * Math.PI * 2, freq: 0.3 + Math.random() * 0.2, amp: 6 + Math.random() * 4 }
                ],
                seed: Math.random() * 100
            });
        }

        pointsRef.current = points;
    };

    // 十六进制转RGBA
    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // 动画循环
    const animate = () => {
        const ctx = ctxRef.current;
        if (!ctx) return;

        const size = sizeRef.current;
        const t = (timeRef.current += 0.006 * config.speed);

        rotationRef.current.y += 0.005 * config.speed;
        rotationRef.current.x += 0.002 * config.speed;

        ctx.clearRect(0, 0, size, size);

        const cx = size / 2;
        const cy = size / 2;
        const scale = (size * 0.38) / 100;
        const projected = [];

        pointsRef.current.forEach((p, i) => {
            let dx = 0,
                dy = 0,
                dz = 0;
            p.offsets.forEach((off) => {
                dx += Math.sin(t * off.freq + off.phase) * off.amp;
                dy += Math.cos(t * off.freq * 0.9 + off.phase) * off.amp;
                dz += Math.sin(t * off.freq * 1.1 + off.phase) * off.amp;
            });

            let x = p.base.x + dx;
            let y = p.base.y + dy;
            let z = p.base.z + dz;

            const cosY = Math.cos(rotationRef.current.y);
            const sinY = Math.sin(rotationRef.current.y);
            let rx = x * cosY - z * sinY;
            let rz = z * cosY + x * sinY;

            const cosX = Math.cos(rotationRef.current.x);
            const sinX = Math.sin(rotationRef.current.x);
            let ry = y * cosX - rz * sinX;
            rz = rz * cosX + y * sinX;

            const fov = 350 / (350 + rz * scale);
            const x2d = rx * scale * fov + cx;
            const y2d = ry * scale * fov + cy;

            const normalizedDepth = (rz + 100) / 200;
            const alpha = Math.max(0.05, Math.pow(1 - normalizedDepth, 1.2));

            projected.push({ x: x2d, y: y2d, z: rz, alpha, fov });

            ctx.beginPath();
            ctx.arc(x2d, y2d, 3.5 * fov, 0, Math.PI * 2);
            ctx.fillStyle = i % 2 === 0 ? config.colorA : config.colorB;
            ctx.globalAlpha = alpha * 0.8;
            ctx.fill();
        });

        const maxDist = 75 * scale;
        ctx.lineCap = 'round';

        for (let i = 0; i < projected.length; i++) {
            const p1 = projected[i];
            for (let j = i + 1; j < projected.length; j++) {
                const p2 = projected[j];
                const dist = Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);

                if (dist < maxDist) {
                    const proximity = 1 - dist / maxDist;
                    const weight = proximity * 1.8 * ((p1.fov + p2.fov) / 2);
                    const lineAlpha = Math.pow(proximity, 2) * ((p1.alpha + p2.alpha) / 2);

                    if (lineAlpha > 0.01) {
                        ctx.beginPath();
                        ctx.lineWidth = weight;
                        ctx.moveTo(p1.x, p1.y);
                        ctx.lineTo(p2.x, p2.y);

                        const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
                        grad.addColorStop(0, hexToRgba(config.colorA, lineAlpha));
                        grad.addColorStop(1, hexToRgba(config.colorB, lineAlpha));

                        ctx.strokeStyle = grad;
                        ctx.stroke();
                    }
                }
            }
        }

        animationRef.current = requestAnimationFrame(animate);
    };

    // 开始动画
    const startAnimation = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        animationRef.current = requestAnimationFrame(animate);
    };

    return (
        <div className={s['loading-wrap']}>
            <div className={s['img-wrap']}>
                <canvas className={s['canvas']} id={canvasId} ref={canvasRef} width={300} height={300} />
                <div id="glow-bg" className={s['glow-bg']}></div>
            </div>

            <div style={{ fontSize: '16px', color: '#000000', marginTop: '12px' }}>{text}</div>
        </div>
    );
};

export default FluidOrb;
